import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useIntlAirports from "../hooks/useIntlAirports";
import { AirplaneLanding, AirplaneTakeoff } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { Airport } from "../@types";

const AirportSelector: FC<{
  onSelect?: (airport: Airport) => void;
  placeholder?: string;
  isDepature?: boolean;
}> = ({ placeholder = "Enter city", isDepature = true, onSelect }) => {
  const { airportsData, isLoading: isFetchingIntlAirports } = useIntlAirports();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState<string>("");

  const filteredList = useMemo(() => {
    if (!airportsData?.data) {
      return [];
    }
    const loweredValue = searchValue.toLowerCase().trim();

    if (loweredValue.trim() === "") {
      return [];
    }

    const result = airportsData?.data?.filter((airport) => {
      return (
        airport.AirportName.toLowerCase().includes(loweredValue) ||
        airport.Country.toLowerCase().includes(loweredValue) ||
        airport.City.toLowerCase().includes(loweredValue) ||
        airport.AirportCode.toLowerCase().includes(loweredValue)
      );
    });

    return result;
  }, [searchValue, airportsData?.data]);

  const handleSelectAirport = useCallback(
    (airport: Airport) => {
      if (onSelect) {
        onSelect(airport);
      }
      setSearchValue(
        `${airport.AirportCode} - ${airport.City}, ${airport.Country}`
      );
    },
    [onSelect]
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="rounded-lg border border-gray-300 shadow px-4 pr-0 w-full flex gap-2 overflow-hidden items-center focus-within:outline ">
        {isDepature ? (
          <AirplaneTakeoff size={20} weight="duotone" />
        ) : (
          <AirplaneLanding size={20} weight="duotone" />
        )}

        <input
          type="text"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setShowDropdown(true);
          }}
          disabled={isFetchingIntlAirports}
          style={{
            cursor: isFetchingIntlAirports ? "progress" : "default",
          }}
          placeholder={
            isFetchingIntlAirports ? "Loading cities..." : placeholder
          }
          className="py-3 focus:outline-none flex-1"
        />
      </div>
      <AnimatePresence>
        {showDropdown && filteredList.length > 0 && (
          <motion.div
            initial={{
              opacity: 0,
              y: -10,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -10,
            }}
            className="absolute z-50 bg-white drop-shadow-2xl top-full mt-2 left-0 w-full rounded-lg border border-gray-300 max-h-96 overflow-y-auto"
          >
            {filteredList.map((airport, index) => {
              return (
                <button
                  onClick={() => handleSelectAirport(airport)}
                  type="button"
                  key={airport.AirportCode + index}
                  tabIndex={index}
                  className="flex flex-col p-2 hover:bg-visaro-primary-700/20 focus:bg-visaro-primary-700/20 cursor-pointer w-full text-left data-[hover]:bg-visaro-primary-700/2 transition-all duration-200"
                >
                  <div>
                    {airport.City}, {airport.Country}
                  </div>
                  <div className="text-sm text-gray-500">
                    {airport.AirportName}
                  </div>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AirportSelector;
